import { api, qs } from './client.js';

/**
 * 서버: modules/shippingFees - 이 시스템의 지역별배송비 표(migrations/023).
 * 메두사몰과 연동하지 않는다. 응답 모양은 원본 메두사몰 화면이 받던 그대로
 * (snake_case)라, 메두사몰 데이터를 옮겨 올 때도 같은 모양을 쓴다.
 */

export type RuleStatus = 'active' | 'inactive';
export type RuleSort = 'amount' | 'priority' | 'updated' | 'name';

export interface SurchargeRule {
  id: string;
  name: string;
  region_pattern: string;
  amount: number;
  priority: number;
  postal_codes: string | null;
  is_active: boolean;
  /** 서버가 대상 지역 표기를 해석할 수 있었는지. false 면 계산에 안 쓰인다. */
  is_valid: boolean;
  normalized_pattern?: string | null;
  /** '시/도'·'시/군/구'·'읍/면/동'·'리' 같은 단위 이름. */
  depth_label?: string | null;
  updated_by?: string;
  updated_at: string | null;
}

export interface RuleList {
  rules: SurchargeRule[];
  count: number;
  counts?: { all: number; active: number; inactive: number };
}

export interface RuleInput {
  name: string;
  region_pattern: string;
  amount: number;
  priority: number;
  is_active: boolean;
  postal_codes: string | null;
}

/** 검사 요청은 숫자로 못 읽은 금액·우선순위를 문자열 그대로 보낸다(서버가 이유를 문구로 준다). */
export interface ValidateInput extends Omit<RuleInput, 'amount' | 'priority'> {
  id?: string;
  amount: number | string;
  priority: number | string;
}

export interface ValidateResult {
  ok: boolean;
  errors: string[];
  warnings: string[];
  normalized: {
    region_pattern: string;
    depth_label: string;
    amount: number;
    priority: number;
    postal_codes: string | null;
  } | null;
}

export interface SavedRule {
  rule: SurchargeRule;
  warnings?: string[];
}

export interface BaseFee {
  base_amount: number | null;
  /** 원본 화면 호환 - 없으면 기본 배송비를 고칠 수 없다. 이 시스템은 언제나 준다. */
  base_price_id: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
  free_threshold: number | null;
  shipping_option_name: string | null;
}

export interface PreviewInput {
  province?: string;
  city?: string;
  address_1?: string;
  address_2?: string;
  jibun_address?: string;
  postal_code?: string;
  item_total?: number;
}

export interface PreviewMatch {
  rule: Pick<SurchargeRule, 'id' | 'name' | 'amount' | 'priority'> & { depth_label?: string | null };
  matched_by: 'address' | 'postal_code' | string;
}

export interface PreviewResult {
  address: { complete: boolean; region: string | null };
  applied: PreviewMatch | null;
  matches: PreviewMatch[];
  inactive_matches: PreviewMatch[];
  base_amount: number;
  surcharge_amount: number;
  total_amount: number;
  free_shipping_applied: boolean;
  item_total: number | null;
  free_threshold: number | null;
}

export const shippingFeesApi = {
  listRules: (p: { q?: string; status?: RuleStatus; sort?: RuleSort; limit: number; offset: number }) =>
    api.get<RuleList>(`/shipping-fees/rules${qs(p)}`),
  createRule: (input: RuleInput) => api.post<SavedRule>('/shipping-fees/rules', input),
  updateRule: (id: string, input: Partial<RuleInput>) =>
    api.patch<SavedRule>(`/shipping-fees/rules/${encodeURIComponent(id)}`, input),
  deleteRule: (id: string) => api.del<void>(`/shipping-fees/rules/${encodeURIComponent(id)}`),
  validate: (input: ValidateInput) => api.post<ValidateResult>('/shipping-fees/validate', input),
  preview: (input: PreviewInput) => api.post<PreviewResult>('/shipping-fees/preview', input),
  baseFee: () => api.get<{ base_fee: BaseFee }>('/shipping-fees/base-fee').then((r) => r.base_fee),
  setBaseFee: (amount: number) =>
    api.put<{ base_fee: BaseFee }>('/shipping-fees/base-fee', { base_amount: amount }).then((r) => r.base_fee),
};
